import React, { useState, useEffect, Fragment, useMemo, useCallback, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import LayoutHOC from "../LayoutHOC";
import DeleteIcon from "../../assets/images/icons/deleteIcon";
import ManageIcon from "../../assets/images/icons/ManageIcon";
import { PlusIcon } from "lucide-react";
import ReactTableComponent from "../UniversalComponents/ReactTableComponent";
import CustomerReactTable from "../UniversalComponents/CustomerReactTable";
import AvatarName from "../UniversalComponents/AvatarName";
import {
  formatNegativeNumber,
  formatTextValue,
  getHexWithOpacity,
  getInitials,
  getTitleCase,
  safeJsonParse,
} from "../../utils/utilityFunctions/stringUtility";
import useFullscreenToggle from "../../hooks/Screen/FullCloseScreen";
import { ReactComponent as AddCustomize } from "../../assets/images/icons/AddCustomize.svg";
import MotionSearchBox from "../UniversalComponents/MotionSearchBox";
import GetPersonAvatar from "../UniversalComponents/GetPersonAvatar";
import HighlightedParagraph from "../UniversalComponents/HighlightedParagraph";
import ResponsiveTags from "../../utils/utilityFunctions/ResponsiveTags";
import { ReactComponent as ThreeDotMenu } from "../../assets/images/threeDotVertical.svg";
import { ReactComponent as CloseModalImg } from "../../assets/images/closeModalImg.svg";
import { ReactComponent as Modules } from "../../assets/images/icons/Modules.svg";
import { ReactComponent as TaskMod } from "../../assets/images/icons/TaskMod.svg";
import { ReactComponent as NoFolderIcon } from "../../assets/images/NoFolderIcon.svg";

import { Dialog, DialogActions, FormControlLabel, Switch } from "@mui/material";
import styled from "@emotion/styled";
import { useFetchAllModualsAndTaskQuery } from "../../utils/company/companypermissions";
import { hideLoader, showLoader } from "../../utils/loaderSlice";
import { pushAlert } from "../../utils/alertSlice";
import InputComponent from "../UniversalComponents/InputComponent";
import { createTemplate } from "../../utils/api/customTemplatesApi";
import { usePostApiMutation } from "../../utils/customer/customerTaskTable";
import { NODE_URL } from "../../utils/constants";
import ToolTipComponent from "../UniversalComponents/ToolTipComponent";
import InternalNavigation from "./InternalNavigation";
import AddItemPlus from "../UniversalComponents/AddItemPlus";
import ToggleSwitch from "../UniversalComponents/SwitchButton";
import moment from "moment";
import Button from "../UniversalComponents/DefaultButton";
import TaskModuleSpace2 from "./TaskModuleSpace2";
import ConfirmationDialogMulti from "../UniversalComponents/ConfirmationDialogMulti";
import { deleteTemplate } from "../../utils/api/customTemplatesApi";
import LabelComponent from "../UniversalComponents/LabelComponent";

const iconList = [
  "bi bi-file-earmark-medical-fill",
  "bi bi-file-earmark-bar-graph-fill",
  "bi bi-clipboard2-data-fill",
  "bi bi-file-arrow-up-fill",
  "bi bi-calendar3-event-fill",
  "bi bi-database-fill",
  "bi bi-building-fill",
  "bi bi-file-medical-fill",
  "bi bi-file-earmark-text-fill",
  "bi bi-kanban-fill",
];

export const predefinedColors = [
  {
    from: "#6417D8",
    to: "#350C72",
  },
  {
    from: "#008937",
    to: "#00230E",
  },
  {
    from: "#9A0D94",
    to: "#340432",
  },
  {
    from: "#EE950F",
    to: "#885509",
  },
  {
    from: "#C7FF6C",
    to: "#779941",
  },
  {
    from: "#124BDA",
    to: "#092874",
  },
  {
    from: "#F8BB76",
    to: "#926E46",
  },
  {
    from: "#00969F",
    to: "#003639",
  },
  {
    from: "#9F000D",
    to: "#390005",
  },
  {
    from: "#FFBBDB",
    to: "#9C426C",
  },
  {
    from: "#815400",
    to: "#1B1200",
  },
  {
    from: "#AEA5FF",
    to: "#686399",
  },
  {
    from: "#B98CA0",
    to: "#6B2141",
  },
  {
    from: "#425F61",
    to: "#88C3C7",
  },
];

const Customization = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { isFullscreen, toggleFullscreen } = useFullscreenToggle();
  const [isDetailsVisible, setDetailsVisible] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [resetPageIndex, setResetPageIndex] = useState(true);
  const [statusValue, setStatusValue] = useState(false);
  const [headerConfigData, setHeaderConfigData] = useState(null);
  const [isCreateNew, setIsCreateNew] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [selectedIcon, setSelectedIcon] = useState(iconList && iconList[0]);
  const [multiData, setMultiData] = useState({});
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [allowMultipleRecords, setAllowMultipleRecords] = useState(false);
  const [visibleScope, setVisibleScope] = useState("customer");
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [newFunnelColor, setNewFunnelColor] = useState(predefinedColors[0]);

  const navItems = [
    {
      label: "My Workspace",
      value: "workspace",
      info: "Create your own modules with custom fields and built-in sections like tasks, notes, and documents.",
    },
    {
      label: "Task Templates",
      value: "task",
      info: "Create and manage reusable task templates to standardize and speed up task creation.",
    },
    // {
    //   label: "Task Templates",
    //   value: "task",
    //   info: "Create and manage reusable task templates to standardize and speed up task creation.",
    // },
  ];

  const getInitialSpace = () => {
    const last = location.pathname.split("/").pop();
    if (last === "task-template") return "task";
    return "workspace";
  };

  const [selectedSpace, setSelectedSpace] = useState(getInitialSpace);

  const {
    data: dataTemplateModuals,
    isLoading: isLoadingTemplateModuals,
    isError: isErrorTemplateModuals,
    error: errorTemplateModuals,
    refetch: refetchTemplateModuals,
  } = useFetchAllModualsAndTaskQuery();
  // 1. Create/Update template
  const [
    AddUpdateTemplate,
    {
      data: templateAddUpdate,
      isLoading: isTemplateAddUpdateLoading,
      isError: isTemplateAddUpdateError,
      isSuccess: isTemplateAddUpdateSuccess,
      error: TemplateError,
    },
  ] = usePostApiMutation();

  useEffect(() => {
    // 🔄 Loading state
    if (isLoadingTemplateModuals) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }

    // ✅ Success case
    if (dataTemplateModuals) {
      if (dataTemplateModuals?.success) {
        setHeaderConfigData(dataTemplateModuals?.data);
      }
    }

    // ❌ Error case
    if (isErrorTemplateModuals) {
      dispatch(
        pushAlert({
          message: errorTemplateModuals?.data?.message || "Failed to fetch modules & tasks",
          type: "danger",
          visible: true,
        })
      );
    }
  }, [isLoadingTemplateModuals, isErrorTemplateModuals, dataTemplateModuals, errorTemplateModuals]);
  // HANDLE ADD/UPDATE TEMPLATE
  useEffect(() => {
    if (isTemplateAddUpdateLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }

    if (isTemplateAddUpdateSuccess && templateAddUpdate?.success) {
      dispatch(pushAlert({ message: templateAddUpdate.message, type: "success", visible: true }));
      handleOrganizationClose();
      navigate(`/setting/customization/task-template/${templateAddUpdate?.data?.template_id}`);
    } else if (isTemplateAddUpdateError) {
      dispatch(
        pushAlert({
          message: TemplateError?.data?.message || "Error adding section",
          type: "danger",
          visible: true,
        })
      );
    }
  }, [isTemplateAddUpdateLoading, isTemplateAddUpdateSuccess, isTemplateAddUpdateError]);

  const IOSSwitch = styled((props) => <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />)(
    ({ theme }) => ({
      width: 42,
      height: 26,
      padding: 0,
      "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: 2,
        transitionDuration: "300ms",
        "&.Mui-checked": {
          transform: "translateX(16px)",
          color: "#fff",
          "& + .MuiSwitch-track": {
            backgroundColor: "#65C466",
            opacity: 1,
            border: 0,
            ...(theme?.palette?.mode === "dark" ? { backgroundColor: "#2ECA45" } : {}),
          },
          "&.Mui-disabled + .MuiSwitch-track": {
            opacity: 0.5,
          },
        },
        "&.Mui-focusVisible .MuiSwitch-thumb": {
          color: "#33cf4d",
          border: "6px solid #fff",
        },
        "&.Mui-disabled .MuiSwitch-thumb": {
          color: theme?.palette?.grey[100],
          ...(theme?.palette?.mode === "dark" ? { color: theme?.palette?.grey[600] } : {}),
        },
        "&.Mui-disabled + .MuiSwitch-track": {
          opacity: 0.7,
          ...(theme?.palette?.mode === "dark" ? { opacity: 0.3 } : {}),
        },
      },
      "& .MuiSwitch-thumb": {
        boxSizing: "border-box",
        width: 22,
        height: 22,
      },
      "& .MuiSwitch-track": {
        borderRadius: 26 / 2,
        backgroundColor: "#E9E9EA",
        opacity: 1,
        transition: theme?.transitions?.create(["background-color"], {
          duration: 500,
        }),
        ...(theme?.palette?.mode === "dark" ? { backgroundColor: "#39393D" } : {}),
      },
    })
  );
  const handleFieldChange = (checked) => {
    setStatusValue(checked);
  };

  const HeaderData = [
    {
      accessor_key: "template_name",
      label: "Template Name",
      type: "text",
    },
    {
      accessor_key: "scope",
      label: "Scope",
      type: "text",
    },
    {
      accessor_key: "created_at",
      label: "Created At",
      type: "date",
    },
    {
      accessor_key: "created_by",
      label: "Created By",
      type: "text",
    },
    {
      accessor_key: "is_deployed",
      label: "Status",
      type: "Boolean",
    },
  ];

  const columns = useMemo(() => {
    const dynamicColumns =
      HeaderData?.map((header) => ({
        accessorKey: header?.accessor_key, // using accessor_key instead of value
        header: header?.label,
        meta: {
          position: "left",
          filterVariant: "select",
          type: header?.type,
          ...header?.meta,
        },
        enableFiltering: false,
        enableColumnFilter: true,
        sortUndefined: "1",

        cell: (info) => {
          const value = info?.getValue();

          const rowData = info?.row?.original;

          if (header?.accessor_key === "template_name") {
            return (
              <span
                onClick={() => {
                  navigate(`/setting/customization/${rowData?.scope === "task" ? "task" : "module"}/${rowData?.id}`);
                }}
                className="cursor-pointer text-[var(--color-primary)] hover:underline"
              >
                <HighlightedParagraph
                  className={"w-full"}
                  paragraph={getTitleCase(value) || value || "-"}
                  searchTerm={globalFilter}
                />
              </span>
            );
          }
          if (header?.accessor_key === "is_deployed") {
            return (
              <div
                className={`flex px-3 py-1 justify-center items-center gap-[10px] rounded-md min-w-8 ${value ? "bg-[#3DB22A]  text-[#FFFFFF]" : "bg-[#E9E9E9] text-[#1A1A1A]"} `}
              >
                {value ? "Deployed" : "Draft"}
              </div>
            );
          }

          if (header?.accessor_key === "scope") {
            return (
              <div className="flex items-start px-2 py-1 rounded-full w-[60px]">
                <HighlightedParagraph className={"w-full"} paragraph={getTitleCase(value)} searchTerm={globalFilter} />
              </div>
            );
          }

          // ✅ Text type clickable
          if (header?.type === "text") {
            return (
              <span
                onClick={() => header?.callingFunction?.(rowData)}
                className="cursor-pointer text-[#1A1A1A] hover:underline"
              >
                <HighlightedParagraph className={"w-full"} paragraph={getTitleCase(value)} searchTerm={globalFilter} />
              </span>
            );
          } else if (header?.type === "number") {
            return (
              <HighlightedParagraph
                className={"w-full"}
                paragraph={formatNegativeNumber(value)}
                searchTerm={globalFilter}
              />
            );
          } else if (header?.type === "date") {
            return (
              <HighlightedParagraph
                className={"w-full"}
                paragraph={value ? moment(value).format("DD MMM YYYY hh:mm A") : "-"}
                searchTerm={globalFilter}
              />
            );
          } else {
            return (
              <span>
                <HighlightedParagraph className={"w-full"} paragraph={getTitleCase(value)} searchTerm={globalFilter} />
              </span>
            );
          }
        },
      })) || [];

    // If columns are not created.
    if (headerConfigData?.length > 0) {
      return [...dynamicColumns];
    }
    return null;
  }, [headerConfigData, globalFilter]);

  const memoizedCustomerData = useMemo(() => {
    // if (globalFilter?.length > 0) {
    // }
    return headerConfigData;
  }, [headerConfigData, globalFilter]);

  const handleOrganizationClose = () => {
    setIsCreateNew(false);
    setTemplateName("");
    setTemplateDescription("");
    setSelectedIcon(iconList[0]);
    setAllowMultipleRecords(false);
    setVisibleScope("customer");
    setNewFunnelColor(predefinedColors[0]);
  };

  const stage = selectedSpace === "task" ? "task" : "workspace";

  const handleCreateTemplate = async () => {
    if (!templateName?.trim()) {
      dispatch(pushAlert({ visible: true, message: "Template Name is required !", type: "danger" }));
      return;
    } else if (!selectedIcon && stage === "workspace") {
      dispatch(pushAlert({ visible: true, message: "Icon is required !", type: "danger" }));
      return;
    }
    let CustomModulePayload = {
      template_name: templateName,
      // meta: {},
      // is_active: 1,
      // is_notes: 0,
      // is_documents: 0,
      // is_task: 0,
      // is_multi_records: 0,
      scope: visibleScope,
      icon: selectedIcon,
      template_description: templateDescription,
      color: newFunnelColor,
      template_type: stage,
    };

    if (stage === "workspace") {
      CustomModulePayload = {
        ...CustomModulePayload,
        is_multi_records: allowMultipleRecords,
        only_company_level: visibleScope === "company" ? true : false,
      };
      const response = await createTemplate({ body: CustomModulePayload });
      if (response?.status) {
        navigate(`/setting/customization/workspace/${response?.template_id}`);
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "danger" }));
      }
    } else if (stage === "task") {
      AddUpdateTemplate({ data: CustomModulePayload, url: `${NODE_URL}template/create` });
    }
  };
  useEffect(() => {
    refetchTemplateModuals();
  }, []);

  const hanldeDeleteCustomTemplate = async () => {
    try {
      const response = await deleteTemplate({ params: { template_id: templateToDelete?.id } });
      if (response.success || response?.status) {
        dispatch(pushAlert({ visible: true, message: response?.message || "Successfull", type: "success" }));
        // navigate("/setting/customization", { replace: true });
        refetchTemplateModuals();
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message || "Something went wrong", type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message || "Something went wrong", type: "danger" }));
    }
  };

  const search = globalFilter?.toLowerCase() || "";

  const filteredData = memoizedCustomerData
    ?.filter((item) => item.template_type === selectedSpace)
    ?.filter((item) => {
      const nameMatch = item?.template_name?.toLowerCase().includes(search);
      const createdByMatch = item?.created_by?.toLowerCase().includes(search);
      return nameMatch || createdByMatch;
    });

  useLayoutEffect(() => {
    if (!selectedSpace) return;

    const target = selectedSpace === "workspace" ? "workspace" : "task-template";

    // ✅ prevent redundant navigation
    if (!location.pathname.endsWith(target)) {
      navigate(`/setting/customization/${target}`, { replace: true });
    }
  }, [selectedSpace, location.pathname]);

  return (
    <>
      <div className="w-full flex flex-col h-full gap-y-2 p-3">
        <div className="flex items-center justify-between pb-3">
          <div className="flex ">
            {navItems?.map((item) => {
              const isActive = selectedSpace === item.value;

              return (
                <button
                  key={item.value}
                  className={`px-3 py-2 text-base rounded-md ${
                    isActive ? "bg-primary-active text-primary" : "bg-white text-gray-600"
                  }`}
                  onClick={() => setSelectedSpace(item?.value)}
                >
                  {item?.label}
                </button>
              );
            })}
          </div>

          <div className={`flex items-center justify-end gap-3`}>
            <div className="flex items-center ">
              <MotionSearchBox
                placeholder={"Search Modules"}
                expanded={false}
                isSearchActive={isSearchActive}
                setIsSearchActive={setIsSearchActive}
                setSearchText={setGlobalFilter}
                searchText={globalFilter}
                ClassName={"rounded-full"}
                outsideClickEffect={false}
              />
            </div>
            <button
              className={`flex items-center gap-2 group    rounded-md  `}
              onClick={() => setIsCreateNew((prev) => !prev)}
            >
              <ToolTipComponent text={"Create New Workspace"} darkMode>
                {/* <AddCustomize className="fill-primary group-hover:fill-white h-4 w-4" /> */}
                <AddItemPlus isActive={isCreateNew} />
              </ToolTipComponent>
            </button>
          </div>
        </div>

        <div className="text-md text-gray-500 italic">
          {navItems?.find((item) => item.value === selectedSpace)?.info}
        </div>

        <div className="flex items-start justify-start gap-6 flex-wrap min-h-0 w-full overflow-auto py-1">
          {filteredData?.length > 0 ? (
            filteredData.map((element, index) => (
              <TaskModuleSpace2
                onClick={() => {
                  navigate(
                    `/setting/customization/${selectedSpace === "workspace" ? "workspace" : "task-template"}/${element?.id}`
                  );
                }}
                space={element}
                key={element.id}
                index={index}
                onDelete={(item) => {
                  setTemplateToDelete(item);
                }}
                globalFilter={globalFilter}
                selectedSpace={selectedSpace}
              />
            ))
          ) : (
            <div className="w-full h-[75vh] flex flex-col items-center justify-center">
              <NoFolderIcon />
              <p className="text-sm text-gray-500 py-4">
                {selectedSpace === "workspace" ? "Module not found." : "Task not found."}
              </p>
            </div>
          )}
        </div>
      </div>

      {isCreateNew && (
        <Dialog
          open={isCreateNew}
          onClose={() => handleOrganizationClose()}
          title={"Create New Workspace"}
          maxWidth="lg"
        >
          <div className="min-h-[30px] w-[30vw] h-full">
            <div className="flex justify-between items-center w-full gap-x-3 py-3 px-4">
              <div className="flex items-center gap-x-2">
                <span className="text-[#131313c9] font-semibold text-xl">Add</span>
                <span className="font-semibold text-xl text-primary">
                  {selectedSpace === "workspace" ? "Workspace" : "Task"}
                </span>
              </div>

              <button
                onClick={() => handleOrganizationClose()}
                type="button"
                className="text-454545 bg-transparent hover:bg-F1F5F8 rounded-lg text-sm  items-center mr-2"
                data-modal-hide="assets-modal"
              >
                <CloseModalImg className="w-3 h-3" />
              </button>
            </div>
            <div className="border w-full"></div>

            <div className="border-none w-full min-h-0 grow flex flex-col items-center justify-start gap-y-3 px-4 py-2">
              <div className="flex gap-3 items-start justify-center w-full">
                {selectedSpace === "workspace" ? (
                  <div
                    className={`flex items-center justify-start h-full w-full gap-3 py-2

    `}
                    // onClick={() => setStage("module")}
                  >
                    <div className="flex flex-col items-start justify-center">
                      <span className="text-sm font-400 text-[#1A1A1A99] italic">
                        A structured space that organizes data, creates tasks, manages notes and documents. It supports
                        every entry with a unified place for related work and collaboration.
                      </span>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`flex items-center justify-start h-full w-full gap-3 
   
       
    `}
                    // onClick={() => setStage("task")}
                  >
                    <div className="flex flex-col items-start justify-center">
                      <span className="text-sm font-400 text-[#1A1A1A99] italic">
                        A collection of ready-made tasks designed to streamline and unify workflows. They enable quick
                        task creation while maintaining process consistency.
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className="w-full flex flex-col gap-2 rounded-md">
                <LabelComponent
                  labelText="Scope"
                  className="font-500 font-Inter text-[#131313CC]"
                  size="medium"
                ></LabelComponent>

                <div className="flex items-center gap-4">
                  {/* Company Option */}
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={visibleScope === "company"}
                      onChange={() => setVisibleScope("company")}
                      className="h-5 w-5 focus:outline-none focus:ring-0 checked:bg-primary border-primary rounded-full text-center"
                    />
                    <span className="text-sm text-[#131313CC]">Company</span>
                  </label>

                  {/* Customer Option */}
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={visibleScope === "customer"}
                      onChange={() => setVisibleScope("customer")}
                      className="h-5 w-5 focus:outline-none focus:ring-0 checked:bg-primary border-primary rounded-full text-center"
                    />
                    <span className="text-sm text-[#131313CC]">Customer</span>
                  </label>
                </div>
              </div>
              <div className="flex flex-col items-start w-full gap-2">
                <div className="w-full">
                  <LabelComponent
                    isMandatory
                    labelText={`${selectedSpace === "workspace" ? "Workspace Name" : "Name"}`}
                    size="medium"
                    className="text-[#131313CC] font-500 font-Inter"
                  ></LabelComponent>
                  <InputComponent
                    type="text"
                    className="w-full rounded-md outline-none mt-1"
                    placeholder={`Enter ${selectedSpace === "workspace" ? "Workspace Name" : "Name"}`}
                    onChange={(e) => setTemplateName(getTitleCase(e?.target?.value || null))}
                    maxLength={20}
                    value={templateName}
                  />
                </div>
                <div className="w-full">
                  <LabelComponent
                    labelText="Description"
                    size="medium"
                    className="font-500 font-Inter text-[#131313CC]"
                  ></LabelComponent>
                  <InputComponent
                    type="text"
                    className="w-full rounded-md outline-none mt-1"
                    placeholder="Enter Description"
                    onChange={(e) => setTemplateDescription(e?.target?.value || null)}
                    maxLength={255}
                    value={templateDescription}
                  />
                </div>
                {selectedSpace === "workspace" && (
                  <div className="flex flex-col gap-2 items-start w-full">
                    <div className="w-full flex items-center justify-between ">
                      <p className="text-sm text-[#131313CC]">Allow Multiple Records</p>
                      <div>
                        <ToggleSwitch
                          // disabled
                          checked={allowMultipleRecords}
                          onChange={(isChecked) => {
                            // handleUpdateTemplatePermission({ label: "onlyCompanyLevel" }, isChecked);
                            setAllowMultipleRecords(isChecked);
                          }}
                          showText={false}
                          fillColor="var(--color-primary)"
                        />
                      </div>
                    </div>
                  </div>
                )}
                <span className="flex flex-col gap-2 items-start w-full">
                  <div className="w-full flex flex-col">
                    <LabelComponent
                      isMandatory
                      labelText="Select Icon"
                      size="medium"
                      className="font-500 font-Inter text-[#131313CC]"
                    ></LabelComponent>

                    {iconList?.length > 0 && (
                      <div className="mt-2 w-full flex items-center justify-evenly gap-x-8 gap-y-3 flex-wrap">
                        {iconList?.map((iconName, index) => (
                          <div
                            className="flex items-center justify-evenly group cursor-pointer"
                            key={index}
                            onClick={() => setSelectedIcon(iconName)}
                          >
                            <i
                              className={`${iconName} text-xl ${iconName === selectedIcon ? "text-[var(--color-primary)]" : "group-hover:text-primary-70"} `}
                            ></i>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="">
                    <LabelComponent
                      className="font-500 font-Inter text-[#131313CC]"
                      labelText="Select color "
                      size={"medium"}
                    />
                    <div className={`py-2`}>
                      <div className="flex flex-wrap justify-start gap-3">
                        {predefinedColors?.map((color, index) => {
                          const isGradient = color?.from && color?.to;

                          return (
                            <button
                              key={index}
                              className={`h-6 w-6 rounded-full border ${
                                newFunnelColor === color ? "ring-2 ring-offset-1 ring-primary-80" : ""
                              }`}
                              style={{
                                background: isGradient
                                  ? `linear-gradient(150deg, ${color?.from} 0%, ${color?.to} 100%)`
                                  : color,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setNewFunnelColor(color);
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </span>
              </div>
            </div>

            <DialogActions>
              <div className="flex border-t w-full items-center justify-end py-2">
                <button
                  onClick={() => handleOrganizationClose()}
                  className="w-fit text-535252 bg-F4F4F4 hover:bg-eeeeee cancel-btn focus:ring-4 focus:ring-blue-300 font-semibold px-5 py-1 text-center rounded-[5px]"
                >
                  Cancel
                </button>
                <Button
                  className="w-fit text-white bg-[var(--color-primary)]save-btn font-semibold border py-1 px-5 ms-3 rounded-[5px]"
                  onClick={() => handleCreateTemplate()}
                  disabled={!templateName || !stage}
                >
                  Next
                </Button>
              </div>
            </DialogActions>
          </div>
        </Dialog>
      )}
      {Boolean(templateToDelete?.id) && (
        <ConfirmationDialogMulti
          open={templateToDelete.id}
          onClose={() => {
            setTemplateToDelete({});
          }}
          title="Delete ?"
          message="This action cannot be undone."
          onConfirm={() => {
            hanldeDeleteCustomTemplate();
          }}
          onCancel={() => {
            setTemplateToDelete({});
          }}
          confirmVariant="delete"
          cancelVariant="secondary"
          confirmText="Delete"
          cancelText="Cancel"
          iconVariant="delete"
        />
      )}
    </>
  );
};

export default Customization;
